// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { getReferenceString } from '@medplum/core';
import type { Practitioner, Schedule } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProviderAppEnvConfig } from '../config/env';
import { getPractitionerTimezone, readWeeklyAvailability, withAllWeekdays } from './availability';
import { SERVICE_TYPE_REFERENCE_EXTENSION_URL } from './constants';
import { createV2AvailabilityRepository, readOverrideDrafts } from './repository';
import type {
  AppointmentConflict,
  AvailabilityOverrideDraft,
  V2AvailabilityDraft,
  V2AvailabilityResources,
} from './types';

export const DEFAULT_BUFFER_AFTER_MINUTES = 10;

interface UseV2AvailabilityEditorStateParams {
  practitioner: Practitioner;
  appointmentTypes: AppointmentTypeDefinition[];
  appointmentTypesLoading?: boolean;
}

interface UseV2AvailabilityEditorStateResult {
  draft: V2AvailabilityDraft;
  setDraft: (draft: V2AvailabilityDraft) => void;
  resetDraft: () => void;
  resources?: V2AvailabilityResources;
  loading: boolean;
  saving: boolean;
  error?: string;
  refresh: () => void;
  loadPastOverrides: () => Promise<AvailabilityOverrideDraft[]>;
  save: () => Promise<V2AvailabilityResources>;
  appointmentConflicts: AppointmentConflict[];
  conflictsLoading: boolean;
  acknowledgeAppointmentConflicts: (acknowledged: boolean) => void;
}

function getInitialTimezone(practitioner: Practitioner): string {
  try {
    return getPractitionerTimezone(practitioner);
  } catch {
    return '';
  }
}

export function useV2AvailabilityEditorState({
  practitioner,
  appointmentTypes,
  appointmentTypesLoading = false,
}: UseV2AvailabilityEditorStateParams): UseV2AvailabilityEditorStateResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const repository = useMemo(() => createV2AvailabilityRepository(medplum), [medplum]);
  const [resources, setResources] = useState<V2AvailabilityResources>();
  const [draft, setDraftState] = useState<V2AvailabilityDraft>({
    timezone: getInitialTimezone(practitioner),
    bufferAfterMinutes: DEFAULT_BUFFER_AFTER_MINUTES,
    healthcareServiceId: '',
    weeklyAvailability: [],
    overrides: [],
  });
  const [loadedDraft, setLoadedDraft] = useState<V2AvailabilityDraft>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [appointmentConflicts, setAppointmentConflicts] = useState<AppointmentConflict[]>([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);

  const createdBy = useMemo(
    () =>
      profile?.resourceType === 'Practitioner' && profile.id
        ? { reference: getReferenceString(profile), display: profile.name?.[0]?.family }
        : undefined,
    [profile]
  );

  const setDraft = useCallback(
    (next: V2AvailabilityDraft | ((current: V2AvailabilityDraft) => V2AvailabilityDraft)) => {
      setDraftState((current) => ({
        ...(typeof next === 'function' ? next(current) : next),
        conflictAcknowledged: false,
      }));
    },
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is a manual reload trigger
  useEffect(() => {
    if (appointmentTypesLoading || appointmentTypes.length === 0) {
      setLoading(appointmentTypesLoading);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(undefined);
    repository
      .loadResources(practitioner, appointmentTypes)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setResources(loaded);
        const timezone = getInitialTimezone(loaded.practitioner);
        const configuredServiceIds = getConfiguredServiceIds(loaded.schedule, loaded.healthcareServices);
        const configuredServiceIdSet = new Set(configuredServiceIds);
        const firstServiceId = configuredServiceIds[0];
        const nextDraft: V2AvailabilityDraft = {
          timezone,
          bufferAfterMinutes:
            getScheduleBufferAfterMinutes(loaded.schedule, firstServiceId) ?? DEFAULT_BUFFER_AFTER_MINUTES,
          healthcareServiceId: firstServiceId ?? '',
          healthcareServiceIds: configuredServiceIds,
          weeklyAvailability: withAllWeekdays(
            loaded.schedule && firstServiceId ? readWeeklyAvailability(loaded.schedule, firstServiceId) : []
          ),
          overrides: timezone
            ? readOverrideDrafts(loaded.overrideSlots, timezone, loaded.healthcareServices).filter((override) =>
                configuredServiceIdSet.has(override.healthcareServiceId)
              )
            : [],
          createdBy,
          conflictAcknowledged: false,
        };
        setDraftState(nextDraft);
        setLoadedDraft(nextDraft);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : String(loadError));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentTypes, appointmentTypesLoading, createdBy, practitioner, refreshKey, repository]);

  useEffect(() => {
    if (!resources || draft.overrides.length === 0 || !draft.timezone) {
      setAppointmentConflicts([]);
      return;
    }
    let cancelled = false;
    setConflictsLoading(true);
    repository
      .findAppointmentConflicts(resources.practitioner, draft, resources.overrideSlots)
      .then((conflicts) => {
        if (!cancelled) {
          setAppointmentConflicts(conflicts);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppointmentConflicts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConflictsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [draft, repository, resources]);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);
  const resetDraft = useCallback(() => {
    if (loadedDraft) {
      setDraft(loadedDraft);
    }
  }, [loadedDraft, setDraft]);

  const loadPastOverrides = useCallback(async (): Promise<AvailabilityOverrideDraft[]> => {
    if (!resources?.schedule) {
      return [];
    }
    const timezone = getInitialTimezone(resources.practitioner);
    if (!timezone) {
      return [];
    }
    const slots = await repository.loadPastOverrideSlots(
      resources.practitioner,
      resources.schedule,
      resources.healthcareServices
    );
    return readOverrideDrafts(slots, timezone, resources.healthcareServices);
  }, [repository, resources]);

  const save = useCallback(async (): Promise<V2AvailabilityResources> => {
    if (!resources) {
      throw new Error('Availability resources are still loading.');
    }
    setSaving(true);
    setError(undefined);
    try {
      const config = getProviderAppEnvConfig();
      const saved = await repository.saveResources(resources, draft, config.medplumOrganizationId, config.polarisEnv);
      setResources(saved);
      setLoadedDraft(draft);
      return saved;
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
      throw saveError;
    } finally {
      setSaving(false);
    }
  }, [draft, repository, resources]);

  const acknowledgeAppointmentConflicts = useCallback((acknowledged: boolean) => {
    setDraftState((current) => ({ ...current, conflictAcknowledged: acknowledged }));
  }, []);

  return {
    draft,
    setDraft,
    resetDraft,
    resources,
    loading,
    saving,
    error,
    refresh,
    loadPastOverrides,
    save,
    appointmentConflicts,
    conflictsLoading,
    acknowledgeAppointmentConflicts,
  };
}

function getConfiguredServiceIds(
  schedule: Schedule | undefined,
  availableServices: V2AvailabilityResources['healthcareServices']
): string[] {
  const availableServiceIds = availableServices.flatMap((service) => (service.id ? [service.id] : []));
  if (!schedule) {
    return availableServiceIds;
  }
  const configuredServiceIds = schedule.serviceType?.flatMap((serviceType) =>
    (serviceType.extension ?? [])
      .filter((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
      .map((extension) => extension.valueReference?.reference?.replace('HealthcareService/', ''))
      .filter((id): id is string => typeof id === 'string' && availableServiceIds.includes(id))
  );
  if (configuredServiceIds?.length) {
    return [...new Set(configuredServiceIds)];
  }

  const configuredServiceCodes = new Set(
    (schedule.serviceType ?? []).flatMap((serviceType) =>
      (serviceType.coding ?? []).flatMap((coding) => (typeof coding.code === 'string' ? [coding.code] : []))
    )
  );
  return availableServices.flatMap((service) => {
    const matches = service.type?.some((type) =>
      type.coding?.some((coding) => typeof coding.code === 'string' && configuredServiceCodes.has(coding.code))
    );
    return matches && service.id ? [service.id] : [];
  });
}

function getScheduleBufferAfterMinutes(
  schedule: V2AvailabilityResources['schedule'],
  serviceId: string | undefined
): number | undefined {
  if (schedule && serviceId) {
    const parameters = schedule.extension?.find(
      (extension) =>
        extension.url === 'https://medplum.com/fhir/StructureDefinition/SchedulingParameters' &&
        extension.extension?.some(
          (child) => child.url === 'service' && child.valueReference?.reference === `HealthcareService/${serviceId}`
        )
    );
    const buffer = parameters?.extension?.find((child) => child.url === 'bufferAfter')?.valueDuration?.value;
    return typeof buffer === 'number' && Number.isFinite(buffer) ? buffer : undefined;
  }
  return undefined;
}

export function updateOverride(
  overrides: AvailabilityOverrideDraft[],
  index: number,
  update: Partial<AvailabilityOverrideDraft>
): AvailabilityOverrideDraft[] {
  return overrides.map((override, currentIndex) => (currentIndex === index ? { ...override, ...update } : override));
}
